import { safeStorage } from '@utils/storage'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { iPharmacy } from 'types/pharmacy'
import { type AnswerMaps, consultationOwner, writeAnswerSet } from './answerSets'

interface PatientStore {
  patient: PatientForm
  pharmacy: iPharmacy | null
  setPharmacy: (pharmacy: iPharmacy | null) => void
  setPatient: (patient: PatientForm) => void
  reset: () => void
  product_id: string | null
  questionnaire_id: string | null
  questionnaire_title: string | null
  setQuestionnaireTitle: (questionnaire_title: string) => void
  setProductId: (product_id: string) => void
  setQuestionnaireId: (questionnaire_id: string) => void
  // Token per shenai question for a scan in progress. It lives here, rather than in the scan URL
  // alone, because it has to survive the consultation reloading after the patient comes back.
  // createdAt lets a token be dropped once its reading could no longer be waiting (see liveShenToken).
  shenTokens: {
    [questionHash: string]: ShenToken
  }
  setShenToken: (questionHash: string, token: string) => void
  clearShenToken: (questionHash: string) => void
  // Unit systems the patient picked on a BMI question, keyed by question hash.
  measurementUnits: {
    [key: string]: MeasurementUnits
  }
  setMeasurementUnits: (questionHash: string, units: MeasurementUnits) => void
  patientHash: string | null
  setPatientHash: (patientHash: string | null) => void
  // patient-before-consult experiment
  // Set when a logged-in patient chose to add another patient over their saved ones before the consultation,
  // so those aren't offered to them again as matches after it.
  isAnotherPatient: boolean
  setIsAnotherPatient: (isAnotherPatient: boolean) => void
  answerHash: string | null
  setAnswerHash: (answerHash: string) => void
  orderHash: string | null
  setOrderHash: (orderHash: string) => void
  productHash: string | null
  setProductHash: (productHash: string) => void
  medicationRequest: string | null
  setMedicationRequest: (medicationRequest: string) => void
  nhsNumber?: string
}

export interface ShenToken {
  token: string
  createdAt: number
}

export type WeightSystem = 'kg' | 'lbs' | 'st-lbs'
export type HeightSystem = 'cm' | 'in' | 'ft-in'

export interface MeasurementUnits {
  weight: WeightSystem
  height: HeightSystem
}

export interface PatientForm {
  firstName: string
  lastName: string
  dob: string
  gender: 'male' | 'female'
}

const initialState: Pick<
  PatientStore,
  | 'patient'
  | 'product_id'
  | 'questionnaire_id'
  | 'answerHash'
  | 'shenTokens'
  | 'measurementUnits'
  | 'patientHash'
  | 'isAnotherPatient'
  | 'orderHash'
  | 'productHash'
  | 'medicationRequest'
  | 'pharmacy'
  | 'questionnaire_title'
> = {
  patient: {
    firstName: '',
    lastName: '',
    dob: '',
    gender: null
  },
  pharmacy: null,
  product_id: null,
  questionnaire_id: null,
  questionnaire_title: null,
  shenTokens: {},
  measurementUnits: {},
  patientHash: null,
  // patient-before-consult experiment
  isAnotherPatient: false,
  answerHash: null,
  orderHash: null,
  productHash: null,
  medicationRequest: null
}

/** Version 1 also recorded the questionnaire request the conversation flow had answered into. */
type LegacyPatientState = Partial<PatientStore & AnswerMaps & { questionnaireRequestHash: string | null }>

/** Moves version 1 answers into the set of the flow they were given in. */
const moveAnswersOut = ({ answers, exitAnswers, flaggedAnswers, hiddenAnswers, optionHashes, questionnaireRequestHash, ...state }: LegacyPatientState) => {
  const maps: AnswerMaps = {
    answers: answers ?? {},
    exitAnswers: exitAnswers ?? {},
    flaggedAnswers: flaggedAnswers ?? {},
    hiddenAnswers: hiddenAnswers ?? {},
    optionHashes: optionHashes ?? {},
    pulses: {}
  }
  const owner = questionnaireRequestHash || (state.questionnaire_id && consultationOwner(state.questionnaire_id))

  if (owner && Object.values(maps).some(map => Object.keys(map).length > 0)) writeAnswerSet(owner, maps)

  return state
}

export const usePatientStore = create<PatientStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setPharmacy: pharmacy => set({ pharmacy }),
      setQuestionnaireTitle: questionnaire_title => set({ questionnaire_title }),
      setPatient: patient => set({ patient }),
      reset: () =>
        set({
          ...initialState,
          measurementUnits: {},
          pharmacy: null,
          product_id: get().product_id,
          questionnaire_id: get().questionnaire_id,
          questionnaire_title: get().questionnaire_title,
          orderHash: null,
          productHash: null,
          medicationRequest: null
        }),
      setProductId: product_id => set({ product_id }),
      setQuestionnaireId: questionnaire_id => set({ questionnaire_id }),
      setShenToken: (questionHash, token) => {
        set({ shenTokens: { ...get().shenTokens, [questionHash]: { token, createdAt: Date.now() } } })
      },
      clearShenToken: questionHash => {
        const { [questionHash]: _, ...shenTokens } = get().shenTokens
        set({ shenTokens })
      },
      setMeasurementUnits: (questionHash, units) => {
        set({ measurementUnits: { ...get().measurementUnits, [questionHash]: units } })
      },
      setPatientHash: patientHash => set({ patientHash }),
      // patient-before-consult experiment
      setIsAnotherPatient: isAnotherPatient => set({ isAnotherPatient }),
      setAnswerHash: answerHash => set({ answerHash }),
      setOrderHash: orderHash => set({ orderHash }),
      setProductHash: productHash => set({ productHash }),
      setMedicationRequest: medicationRequest => set({ medicationRequest })
    }),
    {
      name: 'patient_state',
      storage: createJSONStorage(() => safeStorage),
      version: 2,
      migrate: (persisted, version) => (version < 2 ? moveAnswersOut(persisted as LegacyPatientState) : persisted) as PatientStore,
      skipHydration: true
    }
  )
)

// patient-before-consult experiment
/**
 * Why sending the answers for a patient picked before the consultation failed on its last question, so the
 * patient step can say so without sending them again. Not persisted: a reload shows the plain retry card.
 */
export const usePickedPatientFailure = create<{ error: string; questionnaireIncomplete: boolean }>()(() => ({
  error: '',
  questionnaireIncomplete: false
}))
